import React, { createContext, useContext, useId, useMemo, useRef } from 'react';
import { ListLayout, useListLayout } from './layout';
import '../../settings/sheet/sheet.css';
import './listPage.css';

/**
 * Σελίδα «Πίνακας» (λίστες της νέας εμφάνισης, 14/10/2026): το ίδιο φύλλο και η ίδια κεφαλίδα με τις
 * ρυθμίσεις «Φύλλο» (xs-head: μικρό κεφαλαίο, τίτλος, μία γραμμή), σε όλο το πλάτος και χωρίς ευρετήριο.
 * Από κάτω ListToolbar → DataTable (→ DetailPanel). Όχι κάρτα με εικονίδιο + τίτλο, όχι κουτιά.
 *
 * `embedded`: η ίδια λίστα μέσα σε παράθυρο που έχει ήδη δικό του τίτλο (π.χ. οι Κλήσεις από το μενού
 * του ταμείου) — χωρίς φύλλο και κεφαλίδα, μόνο τα περιεχόμενα.
 */

export interface ListPageContextValue {
  title: string;
  layout: ListLayout;
  embedded: boolean;
}

const ListPageContext = createContext<ListPageContextValue | null>(null);

/** Η σελίδα γύρω από ένα κομμάτι (π.χ. ο DataTable παίρνει από εδώ το όνομά του). null έξω από ListPage. */
export const useListPage = (): ListPageContextValue | null => useContext(ListPageContext);

/** Για το DetailPanel: ό,τι μπαίνει στο συρτάρι δεν ανήκει στη σελίδα από κάτω. */
export const ListPageReset: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ListPageContext.Provider value={null}>{children}</ListPageContext.Provider>
);

export interface ListPageProps {
  /** Μικρό κεφαλαίο πάνω από τον τίτλο: η ομάδα του μενού («Επισκόπηση», «Ταμείο»). */
  eyebrow?: string;
  title: string;
  /** Μία γραμμή κάτω από τον τίτλο. */
  subtitle?: React.ReactNode;
  /** Δεξιά στην κεφαλίδα (δευτερεύουσες ενέργειες· η κύρια μπαίνει στο ListToolbar). */
  actions?: React.ReactNode;
  /** Μέσα σε παράθυρο με δικό του τίτλο: χωρίς φύλλο και κεφαλίδα. */
  embedded?: boolean;
  testId?: string;
  children: React.ReactNode;
}

export const ListPage: React.FC<ListPageProps> = ({
  eyebrow, title, subtitle, actions, embedded = false, testId = 'list-page', children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const layout = useListLayout(rootRef);
  const titleId = useId();
  const ctx = useMemo<ListPageContextValue>(() => ({ title, layout, embedded }), [title, layout, embedded]);

  return (
    <ListPageContext.Provider value={ctx}>
      <div
        ref={rootRef}
        className="lp-wrap"
        data-layout={layout}
        data-embedded={embedded ? 'true' : undefined}
        data-testid={testId}
      >
        {embedded ? (
          // Ο τίτλος είναι του παραθύρου· εδώ μένει ως όνομα της περιοχής για τους αναγνώστες οθόνης.
          <section className="lp-embedded" aria-label={title}>
            {actions && <div className="lp-head-actions lp-embedded-actions">{actions}</div>}
            {children}
          </section>
        ) : (
          <article className="lp-sheet" aria-labelledby={titleId}>
            <header className="xs-head lp-head">
              <div className="lp-head-text">
                {eyebrow && <p className="xs-eyebrow">{eyebrow}</p>}
                <h1 id={titleId} className="xs-title">{title}</h1>
                {subtitle && <p className="xs-subtitle">{subtitle}</p>}
              </div>
              {actions && <div className="lp-head-actions">{actions}</div>}
            </header>
            {children}
          </article>
        )}
      </div>
    </ListPageContext.Provider>
  );
};

export default ListPage;
